/**
 * generateProsumerExcel
 *
 * Generates a multi-sheet .xlsx workbook with all calculation inputs and
 * detailed results for the prosumer tariff calculator.
 *
 * Sheets:
 *   1. Dane wejściowe  — user inputs (profile, consumption, devices)
 *   2. Wyniki          — tariff comparison summary + self-consumption totals
 *   3. Bilans miesięczny — monthly energy balance (production, self-consumption, export, import)
 *   4. Szczegóły rozliczeń — monthly billing details per tariff
 */

import { Workbook } from 'exceljs';
import type { ProsumerFormValues } from '../types/form.types';
import type { ProsumerCalculationResult } from '../types/form.types';
import { OSD_METADATA } from '../../../utils/osdMetadata';

// Polish month names (0-indexed)
const MONTH_NAMES = [
  'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
  'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień',
];

const ORIENTATION_LABELS: Record<string, string> = {
  SOUTH: 'Południe',
  EAST: 'Wschód',
  WEST: 'Zachód',
  FLAT: 'Płaski',
};

const SETTLEMENT_LABELS: Record<string, string> = {
  NET_BILLING: 'Net-billing (depozyt)',
  NET_METERING: 'Net-metering (bank energii)',
};

const RCE_LABELS: Record<string, string> = {
  HOURLY: 'Godzinowe RCE',
  MONTHLY: 'Miesięczne RCE',
};

const CHARGING_LABELS: Record<string, string> = {
  NIGHT: 'Nocne',
  EVENING: 'Wieczorne',
  WEEKEND: 'Weekendowe',
  DAYTIME: 'Dzienne',
  MIXED: 'Mieszane',
};

const INSULATION_LABELS: Record<string, string> = {
  GOOD: 'Dobra',
  MODERATE: 'Średnia',
  POOR: 'Słaba',
};

const PUMP_TYPE_LABELS: Record<string, string> = {
  AIR_SOURCE: 'Powietrzna',
  GROUND_SOURCE: 'Gruntowa',
};

const HEATING_SYSTEM_LABELS: Record<string, string> = {
  UNDERFLOOR: 'Ogrzewanie podłogowe',
  RADIATORS: 'Grzejniki',
};

const CUSTOMER_TYPE_LABELS: Record<string, string> = {
  HOUSEHOLD: 'Gospodarstwo domowe',
  BUSINESS: 'Firma',
};

type CellValue = string | number | null;

/** Build a row of plain cell values */
function row(...values: CellValue[]): CellValue[] {
  return values;
}

/** Round to 2 decimal places to avoid floating-point noise in Excel cells */
function r2(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
}

// ============================================================================
// SHEET 1: Dane wejściowe
// ============================================================================

function buildInputSheet(form: ProsumerFormValues): CellValue[][] {
  const rows: CellValue[][] = [];

  // --- Profile section ---
  rows.push(row('DANE PROFILU', null));
  rows.push(row('Typ odbiorcy', CUSTOMER_TYPE_LABELS[form.customerType] ?? form.customerType));
  rows.push(row('Dystrybutor (OSD)', OSD_METADATA[form.distributor]?.name ?? form.distributor));
  rows.push(row('Rok kalkulacji', form.year));
  rows.push(row('Aktualna taryfa', form.currentTariff || 'Nie podano'));
  rows.push(row(null));

  // --- Consumption section ---
  const methodLabel: Record<string, string> = {
    ANNUAL: 'Roczne zużycie (szacowane)',
    MONTHLY: 'Miesięczne zużycie (ręczne)',
    CSV: 'Import CSV (eLicznik)',
    IMPORTED: 'Zaimportowane dane godzinowe',
  };
  rows.push(row('DANE O ZUŻYCIU', null));
  rows.push(row('Metoda wprowadzania', methodLabel[form.consumptionMethod] ?? form.consumptionMethod));

  // Monthly consumption — always show 12-month breakdown
  const monthly = form.monthlyConsumptionKwh;
  const annualTotal = monthly.reduce((s, v) => s + v, 0);
  rows.push(row('Roczne zużycie łącznie [kWh]', r2(annualTotal)));
  rows.push(row(null));
  rows.push(row('Miesiąc', 'Zużycie [kWh]'));
  for (let i = 0; i < 12; i++) {
    rows.push(row(MONTH_NAMES[i], r2(monthly[i] ?? 0)));
  }

  // If CSV / imported — also show export data
  if (form.csvData?.hasProsumerData) {
    rows.push(row(null));
    rows.push(row('Miesiąc', 'Oddane do sieci [kWh]'));
    for (let i = 0; i < 12; i++) {
      rows.push(row(MONTH_NAMES[i], r2(form.csvData.monthlyExportKwh[i] ?? 0)));
    }
  }
  rows.push(row(null));

  // --- PV section ---
  if (form.hasPV) {
    const pv = form.pvConfig;
    rows.push(row('INSTALACJA FOTOWOLTAICZNA (PV)', null));
    rows.push(row('Moc instalacji [kWp]', pv.capacityKwp));
    rows.push(row('Orientacja dachu', ORIENTATION_LABELS[pv.orientation] ?? pv.orientation));
    rows.push(row('Typ rozliczenia', SETTLEMENT_LABELS[pv.settlementType] ?? pv.settlementType));
    rows.push(row('Cennik RCE', RCE_LABELS[pv.rcePricingChoice] ?? pv.rcePricingChoice));
    rows.push(row('Data uruchomienia', pv.installationDate));
    rows.push(row(null));
  }

  // --- EV section ---
  if (form.hasEV) {
    const ev = form.evConfig;
    rows.push(row('SAMOCHÓD ELEKTRYCZNY (EV)', null));
    rows.push(row('Roczny przebieg [km]', ev.annualMileageKm));
    rows.push(row('Zużycie energii [kWh/100 km]', ev.consumptionKwhPer100km));
    rows.push(row('Wzorzec ładowania', CHARGING_LABELS[ev.chargingPattern] ?? ev.chargingPattern));
    rows.push(row('Moc ładowarki [kW]', ev.chargerPowerKw));
    rows.push(row(null));
  }

  // --- Heat pump section ---
  if (form.hasHeatPump) {
    const hp = form.heatPumpConfig;
    rows.push(row('POMPA CIEPŁA', null));
    rows.push(row('Powierzchnia domu [m²]', hp.houseAreaM2));
    rows.push(row('Izolacja budynku', INSULATION_LABELS[hp.insulationLevel] ?? hp.insulationLevel));
    rows.push(row('Typ pompy', PUMP_TYPE_LABELS[hp.pumpType] ?? hp.pumpType));
    rows.push(row('Podgrzewanie CWU', hp.includesCWU ? 'Tak' : 'Nie'));
    rows.push(row('System ogrzewania', HEATING_SYSTEM_LABELS[hp.heatingSystemType] ?? hp.heatingSystemType));
    rows.push(row(null));
  }

  return rows;
}

// ============================================================================
// SHEET 2: Wyniki — porównanie taryf
// ============================================================================

function buildResultsSheet(form: ProsumerFormValues, result: ProsumerCalculationResult): CellValue[][] {
  const rows: CellValue[][] = [];
  const sc = result.selfConsumption;

  // --- Summary ---
  rows.push(row('PODSUMOWANIE', null));
  rows.push(row('Rekomendowana taryfa', result.recommendedTariff));
  rows.push(row('Maksymalne oszczędności [PLN/rok]', r2(result.maxSavingsPln)));
  rows.push(row('Maksymalne oszczędności [%]', r2(result.maxSavingsPercent)));
  rows.push(row(null));

  // --- Self-consumption totals ---
  rows.push(row('BILANS ENERGETYCZNY (roczny)', null));
  rows.push(row('Całkowita produkcja PV [kWh]', r2(sc.totalProductionKwh)));
  rows.push(row('Autokonsumpcja [kWh]', r2(sc.totalSelfConsumptionKwh)));
  rows.push(row('Energia oddana do sieci [kWh]', r2(sc.totalExportKwh)));
  rows.push(row('Energia pobrana z sieci [kWh]', r2(sc.totalGridImportKwh)));
  rows.push(row('Wskaźnik autokonsumpcji [%]', r2(sc.effectiveRatio * 100)));
  rows.push(row(null));

  // --- Tariff comparison table ---
  rows.push(row('PORÓWNANIE TARYF', null));
  rows.push(row('Taryfa', 'Bez PV [PLN/rok]', 'Z PV [PLN/rok]', 'Oszczędności [PLN/rok]', 'Oszczędności [%]', 'Rekomendacja'));
  for (const tc of result.tariffComparisons) {
    rows.push(row(
      tc.tariffType,
      r2(tc.costWithoutPV),
      r2(tc.costWithPV),
      r2(tc.savingsPln),
      r2(tc.savingsPercent),
      tc.tariffType === result.recommendedTariff ? 'TAK' : '',
    ));
  }
  rows.push(row(null));

  // --- Tariffs compared ---
  rows.push(row('Rok kalkulacji', form.year));
  rows.push(row('Dystrybutor', OSD_METADATA[form.distributor]?.name ?? form.distributor));

  return rows;
}

// ============================================================================
// SHEET 3: Bilans miesięczny
// ============================================================================

function buildMonthlyBalanceSheet(result: ProsumerCalculationResult): CellValue[][] {
  const rows: CellValue[][] = [];
  const sc = result.selfConsumption;

  rows.push(row(
    'Miesiąc',
    'Produkcja PV [kWh]',
    'Autokonsumpcja [kWh]',
    'Oddane do sieci [kWh]',
    'Pobrane z sieci [kWh]',
    'Wskaźnik autokonsumpcji [%]',
  ));

  const prod = sc.monthlyProductionKwh;
  const ratios = sc.monthlyRatios;
  const gridImports = sc.monthlyGridImportKwh;

  for (let i = 0; i < 12; i++) {
    const production = r2(prod[i] ?? 0);
    const ratio = ratios[i] ?? 0;
    const selfConsumed = r2(production * ratio);
    const exported = r2(production * (1 - ratio));
    const gridImport = r2(gridImports[i] ?? 0);
    const ratioPercent = r2(ratio * 100);

    rows.push(row(
      MONTH_NAMES[i],
      production,
      selfConsumed,
      exported,
      gridImport,
      ratioPercent,
    ));
  }

  // Totals row
  const totalProd = r2(sc.totalProductionKwh);
  const totalSelf = r2(sc.totalSelfConsumptionKwh);
  const totalExport = r2(sc.totalExportKwh);
  const totalImport = r2(sc.totalGridImportKwh);
  const avgRatio = r2(sc.effectiveRatio * 100);

  rows.push(row('SUMA / ŚREDNIA', totalProd, totalSelf, totalExport, totalImport, avgRatio));

  return rows;
}

// ============================================================================
// SHEET 4: Szczegóły rozliczeń (monthly billing per tariff)
// ============================================================================

function buildBillingDetailsSheet(result: ProsumerCalculationResult): CellValue[][] {
  const rows: CellValue[][] = [];

  for (const tc of result.tariffComparisons) {
    const monthlyResults = tc.prosumerResult?.monthlyResults;

    rows.push(row(`TARYFA: ${tc.tariffType}`, null));
    rows.push(row('Koszt bez PV [PLN/rok]', r2(tc.costWithoutPV)));
    rows.push(row('Koszt z PV [PLN/rok]', r2(tc.costWithPV)));
    rows.push(row('Oszczędności [PLN/rok]', r2(tc.savingsPln)));
    rows.push(row('Oszczędności [%]', r2(tc.savingsPercent)));
    rows.push(row(null));

    if (monthlyResults && monthlyResults.length > 0) {
      // Detect settlement type from first month's fields
      const firstMonth = monthlyResults[0];
      const isNetBilling = firstMonth.depositBalancePln !== undefined;

      if (isNetBilling) {
        rows.push(row(
          'Miesiąc',
          'Rachunek brutto [PLN]',
          'Saldo depozytu [PLN]',
          'Nowy depozyt [PLN]',
          'Wygasły depozyt [PLN]',
        ));
        for (const mr of monthlyResults) {
          rows.push(row(
            MONTH_NAMES[mr.month] ?? `Miesiąc ${mr.month + 1}`,
            r2(mr.totalBillGrossPln),
            r2(mr.depositBalancePln ?? 0),
            r2(mr.depositCreatedPln ?? 0),
            r2(mr.depositExpiredPln ?? 0),
          ));
        }
      } else {
        // Net-metering
        rows.push(row(
          'Miesiąc',
          'Rachunek brutto [PLN]',
          'Saldo banku energii [kWh]',
          'Zbankowiana energia [kWh]',
          'Wygasła energia [kWh]',
        ));
        for (const mr of monthlyResults) {
          rows.push(row(
            MONTH_NAMES[mr.month] ?? `Miesiąc ${mr.month + 1}`,
            r2(mr.totalBillGrossPln),
            r2(mr.bankBalanceKwh ?? 0),
            r2(mr.bankedKwh ?? 0),
            r2(mr.expiredKwh ?? 0),
          ));
        }
      }
    } else {
      rows.push(row('Szczegóły miesięczne niedostępne dla tej taryfy.', null));
    }

    rows.push(row(null));
  }

  return rows;
}

// ============================================================================
// SHEET 5: Metodologia — how calculations work
// ============================================================================

function buildMethodologySheet(form: ProsumerFormValues): CellValue[][] {
  const settlementType = form.pvConfig?.settlementType ?? 'NET_BILLING';
  const rows: CellValue[][] = [];

  rows.push(row('METODOLOGIA KALKULACJI', null));
  rows.push(row('Wersja kalkulatora', '1.0 — Net-billing / Net-metering'));
  rows.push(row('Rok obliczeń', form.year));
  rows.push(row(null));

  // --- PV production estimate ---
  rows.push(row('1. SZACOWANIE PRODUKCJI PV', null));
  rows.push(row('Metoda', 'Profil miesięczny wg orientacji dachu i lokalizacji (Polska, średnie nasłonecznienie)'));
  rows.push(row('Orientacja → współczynnik roczny',
    'Południe: 1,0 | Wschód/Zachód: 0,8 | Płaski: 0,85'));
  rows.push(row('Wzór roczny', 'Produkcja [kWh] = Moc [kWp] × Nasłonecznienie [kWh/kWp/rok] × współczynnik orientacji'));
  rows.push(row('Rozkład miesięczny', 'Profil udziałów: styczeń–grudzień (suma = 1,0) wg typowych danych dla Polski'));
  rows.push(row('Degradacja paneli', 'Nie jest uwzględniana w tej wersji kalkulatora'));
  rows.push(row(null));

  // --- Self-consumption ---
  rows.push(row('2. AUTOKONSUMPCJA', null));
  rows.push(row('Definicja', 'Energia PV zużyta bezpośrednio przez odbiorcę (nie oddana do sieci)'));
  rows.push(row('Wskaźnik autokonsumpcji', 'Ratio = autokonsumpcja / produkcja PV (0–1)'));
  rows.push(row('Model miesięczny', 'Ratio zależy od stosunku produkcji PV do zużycia w danym miesiącu'));
  rows.push(row('  → wysoka produkcja / niskie zużycie', 'niski wskaźnik (dużo oddajemy do sieci)'));
  rows.push(row('  → niska produkcja / wysokie zużycie', 'wysoki wskaźnik (zużywamy całą produkcję)'));
  rows.push(row(null));

  // --- Settlement ---
  if (settlementType === 'NET_BILLING') {
    rows.push(row('3. ROZLICZENIE: NET-BILLING (system obowiązujący od 1 kwietnia 2022)', null));
    rows.push(row('Podstawa prawna', 'Ustawa OZE z dn. 20.02.2015 r., art. 4 (zm. 2022)'));
    rows.push(row(null));
    rows.push(row('Depozyt prosumencki', null));
    rows.push(row('Tworzenie', 'Energia oddana × cena RCE × współczynnik korekcyjny → depozyt w PLN'));
    rows.push(row('Współczynnik korekcyjny',
      'Styczeń 2025: 1,00 | Od lutego 2025: 1,23'));
    rows.push(row('Cena RCE',
      form.pvConfig?.rcePricingChoice === 'HOURLY'
        ? 'Godzinowe RCE (IRDN — indeks rynku dnia następnego)'
        : 'Miesięczna średnia RCE z TGE (Towarowa Giełda Energii)'));
    rows.push(row(null));
    rows.push(row('Rozliczanie depozytu', null));
    rows.push(row('Offset', 'Depozyt pomniejsza opłatę za energię elektryczną z sieci'));
    rows.push(row('Kolejność FIFO', 'Najstarsze depozyty są wykorzystywane jako pierwsze'));
    rows.push(row('Ważność depozytu', 'Depozyt wygasa po 12 miesiącach od daty powstania'));
    rows.push(row('Nadpłata (zwrot)', form.pvConfig?.rcePricingChoice === 'HOURLY'
      ? 'Niewykorzystany depozyt po 12 mc: zwrot 30% wartości'
      : 'Niewykorzystany depozyt po 12 mc: zwrot 20% wartości'));
    rows.push(row(null));
    rows.push(row('Rachunek miesięczny', null));
    rows.push(row('Składniki', 'Opłata za energię + opłaty dystrybucyjne (stałe i zmienne) + opłaty systemowe'));
    rows.push(row('Depozyt cofa', 'Wyłącznie opłatę za energię — nie dystrybucję, nie system'));
    rows.push(row('VAT', 'Stawka 23% dla energii elektrycznej (dla gospodarstw domowych od 2024 r.)'));
  } else {
    rows.push(row('3. ROZLICZENIE: NET-METERING (bank energii — instalacje sprzed 1 kwietnia 2022)', null));
    rows.push(row('Podstawa prawna', 'Ustawa OZE z dn. 20.02.2015 r., art. 4 (wersja sprzed nowelizacji 2022)'));
    rows.push(row(null));
    rows.push(row('Bank energii', null));
    rows.push(row('Opust (ratio)', form.pvConfig?.capacityKwp != null
      ? `Moc ${form.pvConfig.capacityKwp} kWp → opust ${form.pvConfig.capacityKwp < 10 ? '80%' : '70%'} oddanej energii`
      : 'Moc <10 kWp: opust 80% | Moc ≥10 kWp: opust 70%'));
    rows.push(row('Bankownia energii', 'Za każde 1 kWh oddane do sieci, 0,7 lub 0,8 kWh trafia do banku'));
    rows.push(row('Kolejność FIFO', 'Najstarsza zbankowiana energia jest pobierana jako pierwsza'));
    rows.push(row('Ważność', 'Energia wygasa po 12 miesiącach od daty zbankowienia'));
    rows.push(row(null));
    rows.push(row('Rachunek miesięczny', null));
    rows.push(row('Billable kWh', 'Zużycie z sieci − energia pobrana z banku'));
    rows.push(row('Opłata za energię', 'Naliczana od billable kWh według stawki taryfowej'));
    rows.push(row('Opłaty nieobjęte opustem', 'OZE, kogeneracja, akcyza, część dystrybucji'));
  }
  rows.push(row(null));

  // --- Cost without PV ---
  rows.push(row('4. KOSZT BEZ PV (punkt odniesienia)', null));
  rows.push(row('Metoda', 'Symulacja rocznego rachunku przy zużyciu ze wzorca, bez instalacji PV'));
  rows.push(row('Stawki', 'Dane cennikowe zatwierdzone przez URE dla dystrybutora i roku kalkulacji'));
  rows.push(row(null));

  // --- Recommendation ---
  rows.push(row('5. REKOMENDACJA TARYFY', null));
  rows.push(row('Kryterium', 'Taryfa z najniższym kosztem z PV po uwzględnieniu depozytu / banku energii'));
  rows.push(row('Uwaga', 'Kalkulacja nie uwzględnia historycznych danych RCE — stosowane są dane z roku kalkulacji'));
  rows.push(row(null));

  // --- Disclaimer ---
  rows.push(row('ZASTRZEŻENIA', null));
  rows.push(row('Orientacyjny charakter', 'Wyniki kalkulacji mają charakter szacunkowy i informacyjny.'));
  rows.push(row('Brak gwarancji', 'Rzeczywiste rachunki mogą różnić się w zależności od warunków pogodowych,'));
  rows.push(row(null, 'zmian cennikowych, rozliczeń sieciowych i indywidualnych umów z OSD.'));
  rows.push(row('Źródła danych', 'Taryfy OSD zatwierdzone przez Prezesa URE; dane produkcji PV: własny model na podstawie PVGIS'));
  rows.push(row('Data generowania', new Date().toLocaleDateString('pl-PL')));

  return rows;
}

// ============================================================================
// PUBLIC API
// ============================================================================

const COLUMN_WIDTHS = [38, 22, 22, 24, 22, 28];

/**
 * Generate a prosumer calculation Excel workbook as a Blob.
 *
 * @param form   - Wizard form values (user inputs)
 * @param result - Calculation result from Lambda
 * @returns Promise<Blob> of the .xlsx file
 */
export async function generateProsumerExcel(
  form: ProsumerFormValues,
  result: ProsumerCalculationResult,
): Promise<Blob> {
  const wb = new Workbook();

  const sheetDefs: Array<{ name: string; data: CellValue[][] }> = [
    { name: 'Dane wejściowe', data: buildInputSheet(form) },
    { name: 'Wyniki', data: buildResultsSheet(form, result) },
    { name: 'Bilans miesięczny', data: buildMonthlyBalanceSheet(result) },
    { name: 'Szczegóły rozliczeń', data: buildBillingDetailsSheet(result) },
    { name: 'Metodologia', data: buildMethodologySheet(form) },
  ];

  for (const { name, data } of sheetDefs) {
    const ws = wb.addWorksheet(name);
    ws.addRows(data as (string | number | null)[][]);
    COLUMN_WIDTHS.forEach((width, i) => {
      ws.getColumn(i + 1).width = width;
    });
  }

  const buffer = await wb.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

/**
 * Trigger browser download of prosumer calculation Excel file.
 *
 * @param form   - Wizard form values
 * @param result - Calculation result
 */
export async function downloadProsumerExcel(
  form: ProsumerFormValues,
  result: ProsumerCalculationResult,
): Promise<void> {
  const blob = await generateProsumerExcel(form, result);
  const url = URL.createObjectURL(blob);

  const now = new Date();
  const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
  const timeStr = `${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;
  const filename = `kalkulacja_prosument_${form.year}_${dateStr}_${timeStr}.xlsx`;

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 100);
}
